import React, { FC, Fragment, useState } from 'react';
import { Button, Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import FreeTextFilterComponent from 'components/users/shared/free-text-filter/free-text-filter.component';
import UserTypeFilter from 'components/users/shared/user-type-filter/user-type-filter';
import FilterOnStatusDropdown from 'components/users/shared/filter-on-status-dropdown/filter-on-status-dropdown.component';
import CustomerTypeFilter, { CustomerType } from 'components/users/shared/customer-type-filter/customer-type-filter';

import { OrgDropdown, SuperAdminUserListFilter, UserType } from 'models/user';
import OrgsAutocomplete from '../orgs-autocomplete/orgs-autocomplete';
import UserRoleFilter, { ALL_ROLES, UserRole } from 'components/users/shared/user-role-filter/user-role-filter';

type UserPageActionsProps = {
    onFilterChange: (filters: SuperAdminUserListFilter) => void;
    onOrgChange: (orgId: string | null) => void;
    onAddDentistDialogOpen: () => void;
    onAddTechnicianDialogOpen: () => void;
};

const UserPageActions: FC<UserPageActionsProps> = ({
    onFilterChange,
    onOrgChange,
    onAddDentistDialogOpen,
    onAddTechnicianDialogOpen,
}) => {
    const { t } = useTranslation(['users']);
    const [org, setOrg] = useState<OrgDropdown | null>(null);

    const [filter, setFilter] = useState<SuperAdminUserListFilter>({
        freeText: '',
        active: true,
        local: null,
        isWhiteLabeled: null,
        role: ALL_ROLES,
        type: 'lab_technician',
    });

    const updateFilter = (partial: Partial<SuperAdminUserListFilter>) => {
        const nextType = partial.type ?? filter.type;

        const nextFilter: SuperAdminUserListFilter = {
            ...filter,
            ...partial,
            local: nextType === 'dentist' ? partial.local ?? filter.local : null,
            isWhiteLabeled: nextType === 'dentist' ? partial.isWhiteLabeled ?? filter.isWhiteLabeled : null,
        };

        setFilter(nextFilter);
        onFilterChange(nextFilter);
    };

    const handleFreeTextChange = (freeText: string) => {
        updateFilter({
            freeText,
            local: filter.type === 'dentist' ? filter.local : null,
            isWhiteLabeled: filter.type === 'dentist' ? filter.isWhiteLabeled : null,
        });
    };

    const handleTypeSelection = (type: UserType) => {
        setOrg(null);
        onOrgChange(null);
        updateFilter({
            type,
            local: type === 'dentist' ? false : null,
            isWhiteLabeled: type === 'dentist' ? false : null,
            role: ALL_ROLES,
        });
    };

    const mapCustomerTypeToFilter = (type: CustomerType) => {
        switch (type) {
            case 'ALL':
                return { local: null, isWhiteLabeled: null };
            case 'DENTHUB_PRO':
                return { local: false, isWhiteLabeled: false };
            case 'DENTHUB':
                return { local: false, isWhiteLabeled: true };
            case 'LOCAL':
                return { local: true, isWhiteLabeled: false };
        }
    };

    const apiFilterToCustomerType = (filter: SuperAdminUserListFilter | null | undefined): CustomerType => {
        if (!filter) {
            return 'DENTHUB_PRO';
        }

        if (filter.local) {
            return 'LOCAL';
        }

        if (filter.isWhiteLabeled) {
            return 'DENTHUB';
        }

        return 'DENTHUB_PRO';
    };

    const handleCustomerTypeSelection = (customerType: CustomerType) => {
        if (filter.type === 'dentist') {
            const { local, isWhiteLabeled } = mapCustomerTypeToFilter(customerType);
            updateFilter({ local, isWhiteLabeled });
            return;
        }
        updateFilter({ local: null, isWhiteLabeled: null });
    };

    const handleStatusSelected = (active: boolean | null) => {
        updateFilter({
            active,
        });
    };

    const handleRoleSelected = (role: UserRole) => {
        updateFilter({
            role: role,
        });
    };

    const handleSelectedOrg = (org?: OrgDropdown | null) => {
        if (org) {
            setOrg(org);
            onOrgChange(org._id);
            return;
        }
        setOrg(null);
        onOrgChange(null);
    };

    const getOrgType = () => {
        if (filter.type === 'dentist') {
            return 'clinic';
        }
        if (filter.type === 'lab_technician') {
            return 'lab';
        }
        return null;
    };

    return (
        <Fragment>
            <Box mb={1}>
                {/* Row 1: Filters */}
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <Box minWidth={200} flex={1}>
                        <FreeTextFilterComponent
                            value={filter.freeText}
                            freeTextFilterChange={handleFreeTextChange}
                            label="Filter name"
                        />
                    </Box>

                    <Box minWidth={180}>
                        <UserTypeFilter value={filter.type} fullWidth onTypeSelect={handleTypeSelection} />
                    </Box>

                    <Box minWidth={180}>
                        <UserRoleFilter
                            value={filter.role as UserRole}
                            hasLocalUser={filter.type === 'dentist' ? filter.local : null}
                            fullWidth
                            userType={filter.type}
                            onRoleSelect={handleRoleSelected}
                        />
                    </Box>

                    {filter.type === 'dentist' && (
                        <Box minWidth={200}>
                            <CustomerTypeFilter
                                fullWidth
                                value={apiFilterToCustomerType(filter)}
                                onTypeSelect={handleCustomerTypeSelection}
                            />
                        </Box>
                    )}

                    <Box minWidth={280} flex={1}>
                        <OrgsAutocomplete
                            type={getOrgType()}
                            local={filter.type === 'dentist' ? filter.local : null}
                            isWhiteLabeled={filter.type === 'dentist' ? filter.isWhiteLabeled : null}
                            fullWidth
                            value={org}
                            onOrgChange={handleSelectedOrg}
                        />
                    </Box>

                    <Box minWidth={180}>
                        <FilterOnStatusDropdown fullWidth statusSelected={handleStatusSelected} />
                    </Box>
                </Stack>

                {/* Row 2: Actions */}
                <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                    <Button variant="contained" onClick={onAddDentistDialogOpen} startIcon={<AddIcon />}>
                        {t('users:ADD_DENTIST')}
                    </Button>

                    <Button variant="contained" onClick={onAddTechnicianDialogOpen} startIcon={<AddIcon />}>
                        {t('users:ADD_TECHNICIAN')}
                    </Button>
                </Stack>
            </Box>
        </Fragment>
    );
};

export default UserPageActions;
